import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search, 
  Download,
  Upload,
  LogOut,
  Image as ImageIcon
} from 'lucide-react';
import * as api from '../api';
import { useTelegram } from '../context/TelegramContext';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useTelegram();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admins, setAdmins] = useState([]);
  
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('materials');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Material Form State
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialFormData, setMaterialFormData] = useState({});
  
  // Category Form State
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({});

  const importFileRef = useRef(null);
  const imageUploadRef = useRef(null);
  
  const resetMaterialForm = () => {
    setMaterialFormData({
      title: '', category: '', description: '', link: '',
      duration: '', image: '', tags: '', status: 'published',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '', description: '', imageUrl: ''
    });
  }

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const [materialsData, categoriesData] = await Promise.all([
        api.getMaterials(),
        api.getCategories(),
      ]);
      setMaterials(materialsData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Failed to fetch admin data:", err);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Effect 1: Fetch the list of admins on component mount.
  useEffect(() => {
    const fetchAdminList = async () => {
      try {
        const adminsData = await api.getAdmins();
        setAdmins(adminsData);
      } catch (err) {
        console.error("Failed to fetch admin list:", err);
        setError("Не удалось загрузить список администраторов.");
      }
    };
    fetchAdminList();
  }, []);

  // Effect 2: Check authentication once we have the user and the admin list.
  useEffect(() => {
    // For local development, we optimistically set as authenticated.
    // The server-side check on each API call is the real source of truth.
    if (process.env.NODE_ENV === 'development' && !user) {
      setIsAuthenticated(true);
      return;
    }

    if (user && admins.length > 0) {
      setIsAuthenticated(admins.includes(user.id));
    }
  }, [user, admins]);

  // Effect 3: Fetch all materials and categories if authentication is successful.
  useEffect(() => {
    const runFetch = async () => {
      if (isAuthenticated) {
        await fetchAdminData();
      } else {
        // If not authenticated, ensure we are not in a loading state.
        setLoading(false);
      }
    };

    runFetch();
  }, [isAuthenticated]);

  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || material.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // --- Material Actions ---
  const handleAddMaterial = () => {
    setEditingMaterial(null);
    resetMaterialForm();
    setShowMaterialForm(true);
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setMaterialFormData({
      ...material,
      tags: material.tags?.join(', ') || ''
    });
    setShowMaterialForm(true);
  };

  const handleDeleteMaterial = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот материал?')) {
      try {
        await api.deleteMaterial(id);
        await fetchAdminData();
      } catch (err) {
        alert(`Ошибка удаления: ${err.message}`);
      }
    }
  };

  const handleSaveMaterial = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = {
        ...materialFormData,
        tags: materialFormData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
      };

      if (editingMaterial) {
        await api.updateMaterial(editingMaterial.id, dataToSave);
      } else {
        await api.createMaterial(dataToSave);
      }
      
      setShowMaterialForm(false);
      await fetchAdminData();
    } catch (err) {
      alert(`Ошибка сохранения: ${err.message}`);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const res = await api.uploadImage(file);
      setMaterialFormData(prev => ({...prev, image: res.filePath.replace("http", "https")}));
    } catch (err) {
      alert(`Ошибка загрузки изображения: ${err.message}`);
    }
  };

  // --- Category Actions ---
  const handleAddCategory = () => {
    setEditingCategory(null);
    resetCategoryForm();
    setShowCategoryForm(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData(category);
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = async (id) => {
    if (materials.some(m => m.category === id)) {
      alert('Нельзя удалить категорию, в которой есть материалы.');
      return;
    }
    if (window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
      try {
        await api.deleteCategory(id);
        await fetchAdminData();
      } catch (err) {
        alert(`Ошибка удаления: ${err.message}`);
      }
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.updateCategory(editingCategory.id, categoryFormData);
      } else {
        await api.createCategory(categoryFormData);
      }
      setShowCategoryForm(false);
      await fetchAdminData();
    } catch (err) {
      alert(`Ошибка сохранения категории: ${err.message}`);
    }
  };

  const handleCategoryImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const res = await api.uploadImage(file);
      setCategoryFormData(prev => ({...prev, imageUrl: res.filePath.replace("http", "https")}));
    } catch (err) {
      alert(`Ошибка загрузки изображения: ${err.message}`);
    }
  };

  // --- Import/Export ---
  const handleExport = async () => {
    try {
      await api.exportData();
    } catch (err) {
      alert(`Ошибка экспорта: ${err.message}`);
    }
  };

  const handleImportClick = () => {
    importFileRef.current.click();
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (window.confirm('Вы уверены, что хотите импортировать данные? Существующие данные будут перезаписаны.')) {
          await api.importData(data);
          await fetchAdminData();
          alert('Данные успешно импортированы!');
        }
      } catch (err) {
        alert(`Ошибка импорта: ${err.message}`);
      }
    };
    reader.readAsText(file);
    event.target.value = null;
  };
  
  // --- Render Logic ---
  if (!isAuthenticated && !loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white">
        <h1 className="text-2xl font-bold mb-4">Доступ запрещен</h1>
        <p>У вас нет прав для просмотра этой страницы.</p>
        <button onClick={() => navigate('/')} className="mt-6 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md">
          На главный экран
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="p-4 text-center text-white">Проверка доступа...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto p-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">
              Админ-панель
            </h1>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <LogOut className="w-4 h-4" />
              Выйти в приложение
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('materials')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'materials' 
                ? 'bg-gold-500 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Материалы
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'categories' 
                ? 'bg-gold-500 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Категории
          </button>
        </div>

        {activeTab === 'materials' && !loading && (
          <div className="space-y-6">
            {/* Control Panel */}
            <div className="card p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Left side: buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleAddMaterial}
                    className="btn-primary flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Добавить материал
                  </button>
                </div>

                {/* Right side: filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Поиск материалов..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="input-field pl-10 w-full"
                    />
                  </div>
                  
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="input-field w-full sm:w-48"
                  >
                    <option value="all">Все категории</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Materials Table (Desktop) */}
            <div className="card overflow-hidden hidden md:block">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Материал
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Категория
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Статус
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Дата
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Действия
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredMaterials.map((material) => (
                      <tr key={material.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <img
                              src={material.image}
                              alt={material.title}
                              className="w-10 h-10 rounded-lg object-cover mr-3"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/40x40?text=📄';
                              }}
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {material.title}
                              </div>
                              <div className="text-sm text-gray-500">
                                {material.description.substring(0, 50)}...
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full text-white ${
                            categories.find(c => c.id === material.category)?.color || 'bg-gray-500'
                          }`}>
                            {categories.find(c => c.id === material.category)?.name}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            material.status === 'published' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {material.status === 'published' ? (
                              <>
                                <Eye className="w-3 h-3 mr-1" />
                                Опубликовано
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-3 h-3 mr-1" />
                                Черновик
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(material.date).toLocaleDateString('ru-RU')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditMaterial(material)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteMaterial(material.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Materials List (Mobile) */}
            <div className="space-y-4 md:hidden">
              {filteredMaterials.map((material) => (
                <div key={material.id} className="card p-4">
                  <div className="flex items-start gap-4 mb-4">
                    <img
                      src={material.image}
                      alt={material.title}
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/64x64?text=📄'; }}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1 truncate">{material.title}</h3>
                      <p className="text-sm text-gray-500 line-clamp-2">{material.description}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-medium">Категория</span>
                      <span className={`px-2 py-1 text-xs rounded-full text-white ${
                        categories.find(c => c.id === material.category)?.color || 'bg-gray-500'
                      }`}>
                        {categories.find(c => c.id === material.category)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-medium">Статус</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        material.status === 'published' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {material.status === 'published' ? 'Опубликовано' : 'Черновик'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-medium">Дата</span>
                      <span className="text-gray-800">{new Date(material.date).toLocaleDateString('ru-RU')}</span>
                    </div>
                  </div>
                  
                  <div className="border-t mt-4 pt-3 flex justify-end gap-2">
                    <button onClick={() => handleEditMaterial(material)} className="text-blue-600 hover:text-blue-900 p-2 rounded-md hover:bg-gray-100">
                      <Edit className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleDeleteMaterial(material.id)} className="text-red-600 hover:text-red-900 p-2 rounded-md hover:bg-gray-100">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'categories' && !loading && (
          <div className="space-y-6">
            <div className="card p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Управление категориями
                </h2>
                <button 
                  onClick={handleAddCategory}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Добавить категорию
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <div key={category.id} className="card p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-8 h-8 rounded-full ${category.color} flex items-center justify-center text-white`}>
                        {category.icon}
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleEditCategory(category)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {category.description}
                    </p>
                    <div className="mt-2 text-xs text-gray-500">
                      {materials.filter(m => m.category === category.id).length} материалов
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Material Form Modal */}
      {showMaterialForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {editingMaterial ? 'Редактировать материал' : 'Добавить материал'}
              </h2>
              
              <form onSubmit={handleSaveMaterial} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Название *</label>
                    <input type="text" value={materialFormData.title || ''} onChange={(e) => setMaterialFormData({...materialFormData, title: e.target.value})} className="input-field" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Категория *</label>
                    <select value={materialFormData.category || ''} onChange={(e) => setMaterialFormData({...materialFormData, category: e.target.value})} className="input-field" required>
                      <option value="">Выберите категорию</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
                  <textarea value={materialFormData.description || ''} onChange={(e) => setMaterialFormData({...materialFormData, description: e.target.value})} className="input-field" rows={3} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ссылка на Telegram-пост *</label>
                  <input type="url" value={materialFormData.link || ''} onChange={(e) => setMaterialFormData({...materialFormData, link: e.target.value})} className="input-field" placeholder="https://t.me/club_channel/1234" required />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Длительность</label>
                    <input type="text" value={materialFormData.duration || ''} onChange={(e) => setMaterialFormData({...materialFormData, duration: e.target.value})} className="input-field" placeholder="1:30:00" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Дата</label>
                    <input type="date" value={materialFormData.date || ''} onChange={(e) => setMaterialFormData({...materialFormData, date: e.target.value})} className="input-field" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Обложка</label>
                  <div className='flex items-center gap-4'>
                    <div className='flex-shrink-0 h-16 w-16 rounded-lg bg-gray-100 flex items-center justify-center'>
                      {materialFormData.image ? (
                        <img src={materialFormData.image} alt="Preview" className="h-16 w-16 rounded-lg object-cover" />
                      ) : (
                        <ImageIcon className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    <div className='flex-1'>
                      <input type="file" ref={imageUploadRef} onChange={handleImageUpload} className="hidden" accept="image/*" />
                      <button type="button" onClick={() => imageUploadRef.current.click()} className="btn-secondary w-full mb-2">Загрузить файл</button>
                      <input type="url" value={materialFormData.image || ''} onChange={(e) => setMaterialFormData({...materialFormData, image: e.target.value})} className="input-field" placeholder="Или вставьте URL" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Теги (через запятую)</label>
                  <input type="text" value={materialFormData.tags || ''} onChange={(e) => setMaterialFormData({...materialFormData, tags: e.target.value})} className="input-field" placeholder="мышление, рост, стратегия" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
                  <select value={materialFormData.status || 'published'} onChange={(e) => setMaterialFormData({...materialFormData, status: e.target.value})} className="input-field" >
                    <option value="published">Опубликовано</option>
                    <option value="draft">Черновик</option>
                  </select>
                </div>

                <div className="flex gap-4 pt-4">
                  <button type="submit" className="btn-primary flex-1">{editingMaterial ? 'Сохранить' : 'Добавить'}</button>
                  <button type="button" onClick={() => setShowMaterialForm(false)} className="btn-secondary flex-1">Отмена</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {editingCategory ? 'Редактировать категорию' : 'Добавить категорию'}
              </h2>
              
              <form onSubmit={handleSaveCategory} className="space-y-4">
                <div>
                  <label htmlFor="cat-name" className="block text-sm font-medium text-gray-300">Название</label>
                  <input id="cat-name" type="text" value={categoryFormData.name || ''} onChange={e => setCategoryFormData({...categoryFormData, name: e.target.value})} className="mt-1 block w-full bg-gray-700 border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-white" required />
                </div>
                <div>
                  <label htmlFor="cat-desc" className="block text-sm font-medium text-gray-300">Описание</label>
                  <textarea id="cat-desc" value={categoryFormData.description || ''} onChange={e => setCategoryFormData({...categoryFormData, description: e.target.value})} rows="3" className="mt-1 block w-full bg-gray-700 border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-white"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300">Изображение</label>
                  <div className="mt-1 flex items-center space-x-4">
                    {categoryFormData.imageUrl && <img src={categoryFormData.imageUrl} alt="Preview" className="w-16 h-16 rounded-md object-cover" />}
                    <input type="file" accept="image/*" onChange={handleCategoryImageUpload} className="hidden" ref={imageUploadRef} />
                    <button type="button" onClick={() => imageUploadRef.current.click()} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md flex items-center">
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Загрузить
                    </button>
                  </div>
                </div>
                <div className="flex justify-end space-x-4 pt-4">
                  <button type="button" onClick={() => setShowCategoryForm(false)} className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-md">Отмена</button>
                  <button type="submit" className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-md">Сохранить</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPanel;